// useBookingEdit — PUT /api/v1/collections/bookings/:enc_id wrapper.
//
// The server PUT is a single-vehicle update: every field is optional but
// the body validates as a Zod schema, so we only send the fields that
// changed against the original booking. The hook receives the original
// `BookingDetailResponse.booking` and the current form state and computes
// a minimal diff. This avoids accidentally clearing fields that the form
// re-blanks during edits.

import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queryKeys';
import { useToast } from '../../../hooks/useToast';
import type {
  BookingSummary,
  Timeframe,
  UpdateBookingInput,
} from '../../../types/api';
import type { BookingFormState } from './useBookingForm';

function parsePrice(s: string): number {
  const n = Number.parseFloat(s);
  return Number.isFinite(n) ? n : 0;
}

export function buildEditPayload(
  state: BookingFormState,
  original: BookingSummary,
): UpdateBookingInput {
  const cf = state.customer.fields;
  const v0 = state.vehicles[0];
  const payload: UpdateBookingInput = {};

  // Customer snapshot fields (only on the master row).
  if (cf.name !== original.name) payload.name = cf.name;
  if ((cf.email || null) !== original.email) payload.email = cf.email || null;
  if (cf.phone !== original.phone) payload.phone = cf.phone;
  if ((cf.phone2 || null) !== original.phone2) payload.phone2 = cf.phone2 || null;
  if (cf.address !== original.address) payload.address = cf.address;
  if (cf.postcode !== original.postcode) payload.postcode = cf.postcode;

  const collAddr = state.use_customer_address ? null : state.collection_address || null;
  const collPC = state.use_customer_address ? null : state.collection_postcode || null;
  if (collAddr !== original.collection_address) payload.collection_address = collAddr;
  if (collPC !== original.collection_postcode) payload.collection_postcode = collPC;

  // Vehicle 1 (master).
  if (v0) {
    if (v0.car_make_model !== original.car_make_model)
      payload.car_make_model = v0.car_make_model;
    if (v0.car_registration !== original.car_registration)
      payload.car_registration = v0.car_registration;
    const newPrice = parsePrice(v0.price);
    if (newPrice !== original.price) payload.price = newPrice;
    if (v0.product_id !== original.product_id) payload.product_id = v0.product_id;
  }

  // Booking notes — bound on the page to the prominent "Notes" card
  // (state.notes), NOT to the per-vehicle textarea inside VehicleForm.
  // The bookings table has one notes column per row; in single-vehicle
  // edit mode the canonical input is the booking-level Notes card.
  if ((state.notes || null) !== original.notes) {
    payload.notes = state.notes || null;
  }

  if (state.date !== original.date) payload.date = state.date;
  if (state.timeframe !== original.timeframe)
    payload.timeframe = state.timeframe as Timeframe;
  if (state.driver !== original.driver_id) payload.driver = state.driver;
  if (state.source !== original.source_id) payload.source = state.source;
  if (state.payment_mode !== original.payment_mode_id)
    payload.payment_mode = state.payment_mode;

  if (state.send_email) payload.send_email = true;
  if (state.send_sms) payload.send_sms = true;
  const origReminderEmail = original.reminder_email ?? true;
  const origReminderSms = original.reminder_sms ?? false;
  if (state.reminder_email !== origReminderEmail)
    payload.reminder_email = state.reminder_email;
  if (state.reminder_sms !== origReminderSms)
    payload.reminder_sms = state.reminder_sms;

  return payload;
}

export function useBookingEdit(encId: string | null | undefined, original: BookingSummary | null) {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation<unknown, unknown, BookingFormState>({
    mutationFn: async (state) => {
      if (!encId || !original) {
        throw new Error('useBookingEdit: missing encId or original booking');
      }
      const payload = buildEditPayload(state, original);
      if (Object.keys(payload).length === 0) {
        return { unchanged: true };
      }
      const res = await apiClient.put(endpoints.bookings.detail(encId), payload);
      return res.data;
    },
    onSuccess: async () => {
      if (encId) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.bookings.detail(encId) });
      }
      await queryClient.invalidateQueries({ queryKey: queryKeys.bookings.all });
      // Invalidate all diary sub-keys (today, week, drivers, notes, weekNotes)
      // so edits are reflected immediately in every diary view.
      await queryClient.invalidateQueries({ queryKey: ['diary'] });
      toast.success('Booking updated');
    },
    onError: (err) => {
      const message = axios.isAxiosError(err)
        ? (err.response?.data as { message?: string } | undefined)?.message ??
          'Could not update booking.'
        : 'Could not update booking.';
      toast.error(message);
    },
  });
}
